import { useEffect, useState } from "react";
import { Download, Megaphone, Receipt } from "lucide-react";
import api from "../../lib/api";
import { downloadInvoicePdf } from "../../lib/download";
import Button from "../ui/Button";
import { Card, Badge } from "../ui/Card";
import { formatPrice } from "../../lib/utils";
import MomoPhoneField from "../MomoPhoneField";
import { useAuth } from "../../context/AuthContext";

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

/**
 * Boost switch + what the business owes SHUWA: booking fees it collected
 * in person and Boost commissions, billed on a monthly statement.
 */
export default function ShuwaBalance({ config, onPaid }) {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [chosenProvider, setProvider] = useState("mtn");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const load = () => api.get("/owner/billing").then(({ data }) => setBilling(data)).catch(() => setBilling(null));

  useEffect(() => {
    load();
  }, []);

  if (!billing) return null;

  const providers = [
    ...(config.mobileMoney?.mtn !== false ? [{ id: "mtn", label: "MTN Mobile Money" }] : []),
    ...(config.mobileMoney?.airtel !== false ? [{ id: "airtel", label: "Airtel Money" }] : []),
    ...(config.testMode ? [{ id: "test", label: "Test payment" }] : []),
  ];
  const provider = providers.some((p) => p.id === chosenProvider) ? chosenProvider : providers[0]?.id;
  const openInvoices = billing.invoices.filter((i) => i.status === "open");
  const paidInvoices = billing.invoices.filter((i) => i.status === "paid");

  const toggleBoost = async () => {
    const { data } = await api.put("/owner/billing/boost", { enabled: !billing.boost_enabled });
    setBilling(data);
  };

  const settleNow = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/owner/billing/settle");
      await load();
      setPayingId(data.id);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not create a statement.");
    } finally {
      setBusy(false);
    }
  };

  const pay = async (invoice) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await api.post(`/owner/billing/invoices/${invoice.id}/pay`, {
        provider,
        phone: ["mtn", "airtel"].includes(provider) && phone ? phone : undefined,
      });
      setBilling(data.billing);
      if (data.invoice.status === "paid") {
        setMessage(data.message);
        setPayingId(null);
        // Paying can bring a hidden shop back — let the page refresh its banner.
        onPaid?.();
      } else if (data.invoice.payment_status === "failed") {
        setError(data.message);
      } else {
        setMessage(data.message);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Payment failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Megaphone className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Boost</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Free to switch on. Your shop ranks higher in SHUWA search, and you only pay{" "}
                {billing.boost_percent}% of a new client's first completed visit (max {formatPrice(billing.boost_cap)}) —
                only for clients who found you on SHUWA, not through your own link.
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={billing.boost_enabled}
            aria-label="Boost"
            onClick={toggleBoost}
            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${billing.boost_enabled ? "bg-primary" : "bg-slate-300"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${billing.boost_enabled ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Receipt className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-slate-900">SHUWA balance</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {Number(billing.store_fee_percent) > 0
                ? `When a client books and pays at your store, the ${billing.store_fee_percent}% SHUWA booking fee is added here, with any Boost commissions. Pro shops pay 0%.`
                : "On Pro, bookings paid at your store carry no SHUWA booking fee. Any Boost commissions are added here."}{" "}
              We send a statement on the 1st of each month.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-muted p-3 text-sm">
          <span>Since your last statement</span>
          <span className="font-semibold text-slate-900">{formatPrice(billing.unbilled_total)}</span>
        </div>
        {billing.unbilled_total > 0 && (
          <Button size="sm" variant="outline" className="mt-3" disabled={busy} onClick={settleNow}>
            Settle now
          </Button>
        )}

        {openInvoices.length > 0 && (
          <div className="mt-5 space-y-3">
            {openInvoices.map((invoice) => (
              <div key={invoice.id} className="rounded-xl border border-border p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-slate-900">Statement {invoice.number}</span>
                  <Badge variant={new Date(invoice.due_at) < new Date() ? "warning" : "default"}>Due {formatDate(invoice.due_at)}</Badge>
                </div>
                <ul className="mt-2 space-y-1 text-muted-foreground">
                  {invoice.charges.map((c) => (
                    <li key={c.id} className="flex justify-between gap-2">
                      <span>{c.description}</span>
                      <span>{formatPrice(c.amount)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex items-center justify-between font-semibold text-slate-900">
                  <span>Total</span>
                  <span>{formatPrice(invoice.amount)}</span>
                </div>

                {payingId === invoice.id ? (
                  <div className="mt-3">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {providers.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setProvider(p.id)}
                          className={`rounded-xl border p-2.5 text-left ${provider === p.id ? "border-primary bg-primary/5" : "border-border"}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    {["mtn", "airtel"].includes(provider) && (
                      <MomoPhoneField accountPhone={user?.phone} value={phone} onChange={setPhone} className="mt-2" />
                    )}
                    <Button size="sm" className="mt-3" disabled={busy || invoice.payment_status === "pending"} onClick={() => pay(invoice)}>
                      {busy ? "Processing…" : invoice.payment_status === "pending" ? "Waiting for approval…" : `Pay ${formatPrice(invoice.amount)}`}
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Button size="sm" onClick={() => setPayingId(invoice.id)}>
                      Pay statement
                    </Button>
                    <PdfLink invoice={invoice} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {paidInvoices.length > 0 && (
          <div className="mt-5">
            <p className="mb-2 text-sm font-semibold text-slate-900">Paid statements</p>
            <div className="divide-y divide-border rounded-xl border border-border text-sm">
              {paidInvoices.map((invoice) => (
                <div key={invoice.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <span className="font-medium text-slate-900">{invoice.number}</span>
                  <span className="text-muted-foreground">Paid {formatDate(invoice.paid_at)}</span>
                  <span className="font-medium text-slate-900">{formatPrice(invoice.amount)}</span>
                  <PdfLink invoice={invoice} />
                </div>
              ))}
            </div>
          </div>
        )}

        {message && <p className="mt-3 rounded-xl bg-accent p-3 text-sm text-accent-foreground">{message}</p>}
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      </Card>
    </div>
  );
}

function PdfLink({ invoice }) {
  return (
    <button type="button" onClick={() => downloadInvoicePdf(invoice)} className="flex items-center gap-1 text-sm font-medium text-primary">
      <Download className="h-4 w-4" aria-hidden="true" /> PDF
    </button>
  );
}
